/* global CookieScript */

import React, { useState, useCallback, useEffect, useContext } from 'react';
import Head from 'next/head';
import Script from 'next/script';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import WagtailUserbar from '../../components/WagtailUserbar';
import smoothscroll from 'smoothscroll-polyfill';
import { useTranslation } from 'next-i18next';

import isEmpty from '../../utils/isEmpty';
import { isExternalUrl, isDocumentUrl } from '../../utils/url';

import { useCookieStateContext, CookieStateContext } from './state';
import BaseContext from './BaseContext';
import styles from './BasePage.module.scss';

export default function BasePage({
    navigation,
    footerNavigation,
    children,
    crisisInformation,
    newsletterSignup,
    breadcrumbs,
    meta,
    siteSetting,
    headerVariant = 'default',
    heroTextTheme = 'default',
    wagtailUserbar = null,
    seo,
    map = {},
    rekaiDebug = false,
}) {
    const { t } = useTranslation('common');
    const { siteSwitcher, rekaiId } = siteSetting || {};

    // For handling base context in storybook it is added to all stories, check if
    // storybook settings exists, otherwise it is regular site and context can be created
    const {
        googleMapsApiKey: storybookGoogleMapsApiKey,
        rekaiId: storybookRekaiId,
        isStorybook,
        ...restContext
    } = useContext(BaseContext);

    const [rekaiReady, setRekaiReady] = useState(false);
    const [rekaiAutocompleteReady, setRekaiAutocompleteReady] = useState(false);

    const { googleMapsApiKey } = map;
    const globalGoogleMapsApiKey =
        isStorybook && storybookGoogleMapsApiKey !== '' ? storybookGoogleMapsApiKey : googleMapsApiKey;
    const globalRekaiId = isStorybook && storybookRekaiId !== '' ? storybookRekaiId : rekaiId;
    const baseContext = {
        googleMapsApiKey: globalGoogleMapsApiKey,
        isStorybook,
        rekaiId: globalRekaiId || null,
        rekaiReady,
        setRekaiReady,
        rekaiAutocompleteReady,
        setRekaiAutocompleteReady,
    };

    const context = useCookieStateContext();
    const { handleCookieAcceptAll, handleCookieAcceptCategories, handleCookieRejectAll } = context;

    useEffect(() => {
        if (meta && typeof window !== 'undefined') {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                contentgrouping: {
                    area: meta.metaDataArea || '',
                    region: meta.metaDataRegions || '',
                    program: meta.metaDataInternalProgram || '',
                    funding: meta.metaDataFunding || '',
                },
            });
        }
    }, [meta?.metaDataArea, meta?.metaDataFunding, meta?.metaDataInternalProgram, meta?.metaDataRegions]);

    useEffect(() => {
        smoothscroll.polyfill();
    }, []);

    useEffect(() => {
        /*
         * Open external links in new windows
         * this is against accessibility and best practice rules
         * but a requirement for this project
         */
        const links = document.querySelectorAll('a[href]');
        for (let i = 0; i < links.length; i++) {
            const isExternal = isExternalUrl(links[i].getAttribute('href'));
            const isDownload = isDocumentUrl(links[i].getAttribute('href'));

            if (isExternal) {
                links[i].setAttribute('target', '_blank');
                links[i].setAttribute('rel', 'noopener');
            } else if (isDownload && !links[i].hasAttribute('target') && !links[i].hasAttribute('download')) {
                links[i].setAttribute('target', '_blank');
            }
        }
    }, []);

    useEffect(() => {
        const handleCookieScriptLoaded = () => {
            handleCookieAcceptCategories(CookieScript.instance.currentState().categories);
        };

        if (window.CookieScript && window.CookieScript.instance) {
            handleCookieScriptLoaded();
        }

        window.addEventListener('CookieScriptLoaded', handleCookieScriptLoaded);

        window.addEventListener('CookieScriptAccept', (e) => {
            const categories = e.detail?.categories;
            handleCookieAcceptCategories(categories);

            if (typeof gtag === 'function' && categories.includes('targeting')) {
                window.dataLayer.push({ event: 'consentGranted' });
            }
        });

        window.addEventListener('CookieScriptAcceptAll', () => {
            handleCookieAcceptAll();
            typeof gtag === 'function' && window.dataLayer.push({ event: 'consentGranted' });
        });

        window.addEventListener('CookieScriptReject', () => {
            handleCookieRejectAll();
        });

        window.addEventListener('CookieScriptLoaded', function (e) {
            console.log('Global: CookieScriptLoaded event detected', e);
        });

        return () => {
            window.removeEventListener('CookieScriptLoaded', handleCookieScriptLoaded);
            window.removeEventListener('CookieScriptAccept', handleCookieAcceptCategories);
            window.removeEventListener('CookieScriptAcceptAll', handleCookieAcceptAll);
            window.removeEventListener('CookieScriptReject', handleCookieRejectAll);
        };
    }, []);

    const showBreadcrumbs = breadcrumbs && breadcrumbs.items && breadcrumbs.items.length > 1;

    const classes = classNames(styles['BasePage'], {
        [styles['BasePage--Breadcrumbs']]: showBreadcrumbs,
    });

    return (
        <>
            <Meta {...seo} />

            <BaseContext.Provider value={baseContext}>
                <CookieStateContext.Provider value={context}>
                    {globalRekaiId && rekaiDebug && (
                        <Script
                            id="rekai-test-env"
                            strategy="beforeInteractive"
                            dangerouslySetInnerHTML={{
                                __html: `
                                    document.cookie = 'rekblock=1';
                                `,
                            }}
                        />
                    )}

                    <div className={classes} role="document">
                        <a className={styles['BasePage__SkipMain']} href="#main-content">
                            {t('base.skip')}
                        </a>
                        <header className={styles['BasePage__Header']}>
                            <Header
                                navigation={navigation}
                                headerVariant={headerVariant}
                                crisisInformation={crisisInformation}
                                heroTextTheme={heroTextTheme}
                            />
                        </header>
                        <main className={styles['BasePage__Main']}>{children}</main>

                        <Footer {...footerNavigation} siteSwitcher={siteSwitcher} newsletterSignup={newsletterSignup} />
                        {wagtailUserbar && <WagtailUserbar {...wagtailUserbar} />}
                    </div>
                </CookieStateContext.Provider>
            </BaseContext.Provider>
        </>
    );
}

BasePage.propTypes = {
    children: PropTypes.node,
    navigation: PropTypes.object,
    newsletterSignup: PropTypes.object,
    footerNavigation: PropTypes.object,
    crisisInformation: PropTypes.object,
    breadcrumbs: PropTypes.object,
    meta: PropTypes.object,
    siteSetting: PropTypes.object,
    headerVariant: PropTypes.string,
    heroTextTheme: PropTypes.string,
    wagtailUserbar: PropTypes.object,
};

const Meta = ({
    seoHtmlTitle = '',
    seoMetaDescription = '',
    seoOgUrl = '',
    seoOgDescription = '',
    seoOgTitle = '',
    seoOgImage = null,
    seoOgType = '',
    seoTwitterTitle = '',
    seoTwitterDescription = '',
    seoTwitterUrl = '',
    seoTwitterImage = null,
    seoMetaRobots = '',
    seoCanonicalLink = '',
    rekTitle = '',
    rekDescription = '',
    rekImage = {},
    rekPubdate = '',
    rekModdate = '',
    rekEnddate = '',
    ldJson = [],
}) => {
    return (
        <Head>
            <title>{seoHtmlTitle}</title>

            {!!seoMetaDescription && <meta name="description" content={seoMetaDescription} />}

            {!!seoOgUrl && <meta property="og:url" content={seoOgUrl} />}

            {!!seoOgDescription && <meta property="og:description" content={seoOgDescription} />}

            {!!seoOgTitle && <meta property="og:title" content={seoOgTitle} />}

            {!!seoOgImage && <meta property="og:image" content={seoOgImage} />}

            {!!seoOgType && <meta property="og:type" content={seoOgType} />}

            {!!seoTwitterTitle && <meta property="twitter:title" content={seoTwitterTitle} />}

            {!!seoTwitterDescription && <meta property="twitter:description" content={seoTwitterDescription} />}

            {!!seoTwitterUrl && <meta property="twitter:url" content={seoTwitterUrl} />}

            {!!seoTwitterImage && <meta property="twitter:image" content={seoTwitterImage} />}

            {!!seoMetaRobots && <meta name="robots" content={seoMetaRobots} />}

            <link rel="canonical" href={seoCanonicalLink} />

            {!!rekTitle && <meta name="rek:title" content={rekTitle} />}
            {!!rekDescription && <meta name="rek:description" content={rekDescription} />}
            {!!rekImage && rekImage?.src && (
                <>
                    <meta name="rek:image" content={rekImage?.src} />
                    <meta name="rek:image:alt" content={rekImage?.alt} />
                </>
            )}
            {!!rekPubdate && <meta property="rek:pubdate" content={rekPubdate} />}
            {!!rekModdate && <meta property="rek:moddate" content={rekModdate} />}

            {/* Last day this page shall be recommended */}
            {!!rekEnddate && <meta property="rek:enddate" content={rekEnddate} />}

            {ldJson &&
                ldJson.map((content, index) => (
                    <script key={index} type="application/ld+json" dangerouslySetInnerHTML={{ __html: content }} />
                ))}
        </Head>
    );
};

Meta.propTypes = {
    seoHtmlTitle: PropTypes.string,
    seoMetaDescription: PropTypes.string,
    seoOgUrl: PropTypes.string,
    seoOgDescription: PropTypes.string,
    seoOgTitle: PropTypes.string,
    seoOgImage: PropTypes.string,
    seoOgType: PropTypes.string,
    seoTwitterTitle: PropTypes.string,
    seoTwitterDescription: PropTypes.string,
    seoTwitterUrl: PropTypes.string,
    seoTwitterImage: PropTypes.string,
    seoMetaRobots: PropTypes.string,
    seoCanonicalLink: PropTypes.string,
    rekTitle: PropTypes.string,
    rekDescription: PropTypes.string,
    rekImage: PropTypes.object,
    rekPubdate: PropTypes.string,
    rekModdate: PropTypes.string,
    rekEnddate: PropTypes.string,
    ldJson: PropTypes.array,
};
